/**
 * @format
 */

import React, { useState, useEffect, useRef } from 'react';
import styles from './Login.module.scss';
import ClearIcon from '@mui/icons-material/Clear';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { signInUser } from '../../redux/actionCreators/user';
import { toggleTheme } from '../../redux/actionCreators/theme';
import { store } from '../../redux/store';
import {
	Box,
	Button,
	TextField,
	Typography,
	InputAdornment,
} from '@mui/material';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import {
	forbiddenChars,
	OPERATIONS,
	REDIRECT_URLS,
	DARK_MODE,
	LIGHT_MODE,
	WORK_STREAM_TYPE,
	AT_STATION_TAB,
} from '../../constants';
import { getNavigableOperations, getCurrentPartStatus, getNextOperation } from '../../utils/operationFlow';
import { updateTab } from '../../redux/actionCreators/tab';
import { updateCurrentLineItem } from '../../redux/actionCreators/station';
import { updateOperations } from '../../redux/actionCreators/operations';

// Images
import lightToggleImg from '../../assets/images/Light_.png';
import darkToggleImg from '../../assets/images/Dark.png';
import rangeBlack from '../../assets/images/range-rover-black.png';
import rangeLight from '../../assets/images/Rage-rover-light.png';
import logoDark from '../../assets/images/JLR_TataMotors.png';
import logoLight from '../../assets/images/JLR_Light.jpg';
import usernameImg from '../../assets/images/Username.png';
import passwordImg from '../../assets/images/Password.png';
import cardDecor from '../../assets/images/Rectangle-Card.png';
import { encryptStationPassword } from '../../utils/EncryptionUtils';
import MessageButton from '../../components/MessageButton/MessageButton';

const mapStateToProps = (state) => ({
	user: state.user,
	allowedOperations: state.operations?.allowedOperations || [],
	workStreamType: state.user?.workStreamType,
	themeMode: state.theme.themeMode,
});

const mapDispatchToProps = (dispatch) => ({
	signInUser: (credentials, successCallback, errorCallback, conflictCallback) =>
		dispatch(signInUser(credentials, successCallback, errorCallback, conflictCallback)),
	toggleTheme: (mode) => dispatch(toggleTheme(mode)),
});

export const Login = ({
	signInUser,
	allowedOperations,
	workStreamType,
	themeMode,
	toggleTheme,
}) => {
	const navigate = useNavigate();
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState('');
	const inputRef = useRef(null);

	const { showMessageOverlay } = useMessageContextManager();

	const isDark = themeMode === DARK_MODE;

	const handleThemeToggle = () => {
		const newMode = isDark ? LIGHT_MODE : DARK_MODE;
		toggleTheme(newMode);
	};

	useEffect(() => {
  window.__LOGIN_SCREEN__ = true;

  return () => {
    window.__LOGIN_SCREEN__ = false;
  };
}, []);
	const showMismatchPopup = () => {
  showMessageOverlay({
    error: true,
    contextText: 'Login Failed',
    info: 'User ID or Password is mismatched',

		  priority: true,
    blockGlobal: true,
    render: ({ close, palette, buttonGradient }) => (
      <MessageButton
        variant="contained"
        label="OK"
        onClick={close}
        themeMode={themeMode}
        palette={palette}
        buttonGradient={buttonGradient}
      />
    ),
  });
};

	const successCallback = (res) => {
		// Wait a bit for Redux to update with operations, then navigate based on first operation
		setTimeout(async () => {
			// Keep At Station tab active before routing to /scan-vin; otherwise TabPanel hides content.
			store.dispatch(updateTab({ tabValue: AT_STATION_TAB }));

			// Get operations from Redux store (they should be set by now)
			const state = store.getState();
			const ops = state.operations?.allowedOperations || allowedOperations || [];

			if (ops.length > 0) {
				if (ops.includes(OPERATIONS.MAJOR_REPAIR)) {
					store.dispatch(updateOperations({ currentOperation: OPERATIONS.MAJOR_REPAIR }));
					navigate('/assembly/major-repair/receive-part');
					return;
				}

				const navigableOps = getNavigableOperations(ops);
				const firstOperation = navigableOps.includes(OPERATIONS.RECEIVE_SN)
					? OPERATIONS.RECEIVE_SN
					: navigableOps[0];

				// If RECEIVE_SN is available, check if there's an in-progress part
				// before defaulting to scan-vin. This handles the case where the user
				// re-logs in mid-flow (e.g. RECEIVE_SN done, ENGRAVE pending).
				if (firstOperation === OPERATIONS.RECEIVE_SN) {
					try {
						const currentPart = await getCurrentPartStatus();
						if (currentPart?.serialNumber) {
							// For ENGRAVE stations: if a part exists, always go to engraving
							// regardless of whether all operations are already completed.
							if (ops.includes(OPERATIONS.ENGRAVING)) {
								store.dispatch(updateCurrentLineItem(currentPart));
								store.dispatch(updateOperations({ currentOperation: OPERATIONS.ENGRAVING }));
								navigate(REDIRECT_URLS[OPERATIONS.ENGRAVING]);
								return;
							}

							if (currentPart?.completedOperations?.includes(OPERATIONS.RECEIVE_SN)) {
								const nextOp = getNextOperation(currentPart.completedOperations, ops);
								const nextPath = REDIRECT_URLS[nextOp];
								if (nextPath && nextPath !== REDIRECT_URLS[OPERATIONS.RECEIVE_SN]) {
									store.dispatch(updateCurrentLineItem(currentPart));
									store.dispatch(updateOperations({ currentOperation: nextOp }));
									navigate(nextPath);
									return;
								}
							}
						}
					} catch (e) {
						// API failure — fall through to scan-vin
					}
					navigate(REDIRECT_URLS[OPERATIONS.RECEIVE_SN]);
					return;
				}

				// If DROP is first, handle based on work stream type
				if (firstOperation === OPERATIONS.DROP) {
					const wsType = state.user?.workStreamType || workStreamType;
					if (wsType === WORK_STREAM_TYPE.POOL) {
						navigate('/scan-vin');
					} else {
						navigate('/');
					}
					return;
				}

				// Otherwise, use the redirect URL for the first operation
				const redirectUrl = REDIRECT_URLS[firstOperation];
				if (redirectUrl) {
					navigate(redirectUrl);
					return;
				}
			}

			// Fallback: navigate to a route that will trigger RedirectToLineItemState
			navigate('/scan-vin');
		}, 100);
	};

	const onContinue = () => {
		if (!username) return setError('Username is required');
		if (!password) return setError('Password is required');

		const encryptedPassword = encryptStationPassword(password);
		signInUser(
  { username, password: encryptedPassword, signInType: 'F' },
  successCallback,
    () => showMismatchPopup(),
  (res) => {},
);
	};

	const onConflict = () => {
		showMessageOverlay({
			error: true,
			contextText: 'Multiple sessions detected',
			info: 'You have multiple active sessions, do you want to logout from previous sessions to continue',
			render: ({ close, palette, buttonGradient }) => (
				<>
					<MessageButton
						variant="outlined"
						label="Cancel"
						onClick={close}
						themeMode={themeMode}
						palette={palette}
						buttonGradient={buttonGradient}
					/>
					<MessageButton
						variant="contained"
						label="Continue"
						onClick={()=>{
              onContinue()
              setTimeout(()=>{
                close()
              }, 1000)
            }}
						themeMode={themeMode}
						palette={palette}
						buttonGradient={buttonGradient}
					/>
				</>
			),
		});
	};

	const signIn = (e) => {
		e.preventDefault();
		if (!username) return setError('Username is required');
		if (!password) return setError('Password is required');

		const encryptedPassword = encryptStationPassword(password);
	signInUser(
  { username, password: encryptedPassword, signInType: 'C' },
  successCallback,
   () => showMismatchPopup(),
  onConflict,
);
	};

	useEffect(() => {
		if (error) {
			setUsername('');
			setPassword('');
			inputRef.current?.focus();
		}
	}, [error]);

	// Apply theme to document for CSS custom properties
	useEffect(() => {
		document.documentElement.setAttribute('data-theme', themeMode);
	}, [themeMode]);

	const toggleImg = isDark ? lightToggleImg : darkToggleImg;
	const rootThemeClass = isDark ? styles.darkTheme : styles.lightTheme;

	return (
		<div className={`${styles.root} ${rootThemeClass}`}>
			{/* Theme Toggle */}
			<button
				className={styles.toggleButton}
				onClick={handleThemeToggle}
				aria-label="Toggle theme"
			>
				<img src={toggleImg} alt="toggle theme" />
			</button>

			<div
				className={`${styles.container} ${
					isDark ? styles.darkContainer : styles.lightContainer
				}`}
			>
				{/* Left Section - Car */}
				<div
					className={`${styles.carSection} ${
						isDark ? styles.darkCar : styles.lightCar
					}`}
					style={{
						'--car-bg-image': `url(${isDark ? rangeBlack : rangeLight})`,
					}}
				/>

				{/* Right Section - Login */}
				<div className={styles.loginSection}>
					<Box
						className={`${styles.loginCard} ${
							isDark ? styles.darkCard : styles.lightCard
						}`}
					>
						{!isDark && (
							<img
								className={styles.topRightImage}
								src={cardDecor}
								alt="decor"
							/>
						)}

						<img
							src={isDark ? logoDark : logoLight}
							alt="JLR - Logo"
							className={styles.logo}
						/>

						{/* Subtitle */}
						<Typography className={styles.title}>
							Assembly Management
						</Typography>

						{/* Form */}
						<form className={styles.form} onSubmit={signIn} noValidate>
							<div
								className={`${styles.inputWrapper} ${
									isDark ? styles.darkInput : ''
								}`}
							>
								<div className={styles.inputField}>
									<img
										src={usernameImg}
										alt="user"
										className={styles.inputIcon}
									/>
									<input
										type="text"
										placeholder="User Name"
										value={username}
										ref={inputRef}
										onChange={(e) =>
											setUsername(e.target.value.replace(forbiddenChars, ''))
										}
									/>
								</div>
							</div>

							<div
								className={`${styles.inputWrapper} ${
									isDark ? styles.darkInput : ''
								}`}
							>
								<div className={styles.inputField}>
									<img
										src={passwordImg}
										alt="lock"
										className={styles.inputIcon}
									/>
									<input
										type="password"
										placeholder="Password"
										value={password}
										onChange={(e) =>
											setPassword(e.target.value.replace(forbiddenChars, ''))
										}
									/>
								</div>
							</div>

							{error && (
								<Typography className={styles.error}>
									<ClearIcon className={styles.errorIcon} />
									{error}
								</Typography>
							)}

							<Button
								type="submit"
								variant="contained"
								disableElevation
								className={`${styles.loginButton} ${
									isDark ? styles.darkButton : ''
								}`}
							>
								Login
							</Button>
						</form>

						{/* <div className={`${styles.forgotPassword} ${isDark ? styles.darkText : ''}`}>
              Forgot Password?
            </div> */}
					</Box>
				</div>
			</div>
		</div>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);
